import React, { forwardRef } from 'react';
import {
    type GetProps,
    Input as BaseInput,
    isWeb,
    type StackProps,
    View,
    XStack,
} from 'tamagui';

import { borderCurve } from '../componentHelpers';
import { Icon, type IconType } from '../icons/Icon';

export interface InputProps extends GetProps<typeof BaseInput> {
    disabled?: boolean;
    invalid?: boolean;
    leftIcon?: IconType;
    rightIcon?: IconType;
    fontWeight?: 'regular' | 'emphasized';
    label?: string;
    onPressIcon?: () => void;
    onPressLeftIcon?: () => void;
    size?: 'regular' | 'tiny';
    containerProps?: StackProps;
}

export const Input = forwardRef<BaseInput, InputProps>(
    (
        {
            fontWeight = 'emphasized',
            disabled,
            invalid,
            leftIcon,
            rightIcon,
            width,
            onPressIcon,
            onPressLeftIcon,
            size = 'regular',
            containerProps,
            multiline,
            ...props
        },
        ref,
    ) => {
        const isMultiline = multiline === true;
        const fontWeightToken =
            fontWeight === 'regular'
                ? '$1'
                : fontWeight === 'emphasized'
                  ? '$2'
                  : undefined; // whatever the default is
        const basePadding = size === 'tiny' ? 8 : 16;
        const iconSpacing = 30; // Similar spacing to rightIcon
        return (
            <XStack
                width={width ?? '100%'}
                minWidth={isMultiline ? 0 : undefined}
                alignItems={isMultiline ? 'flex-start' : 'center'}
                position="relative"
                {...containerProps}
            >
                {leftIcon != null && (
                    <View
                        position="absolute"
                        left={basePadding}
                        height={24}
                        alignItems="center"
                        justifyContent="center"
                        zIndex={1}
                        onPress={onPressLeftIcon}
                        {...(onPressLeftIcon ? { cursor: 'pointer' } : {})}
                    >
                        <Icon icon={leftIcon} color="$gray1100" size={16} />
                    </View>
                )}
                <BaseInput
                    ref={ref}
                    autoCapitalize="none"
                    blurOnSubmit={false}
                    flex={1}
                    width={isMultiline ? '100%' : undefined}
                    minWidth={isMultiline ? 0 : undefined}
                    height={size === 'tiny' ? 32 : 56}
                    padding={basePadding}
                    paddingLeft={
                        leftIcon != null
                            ? basePadding + iconSpacing
                            : basePadding
                    }
                    fontSize={size === 'tiny' ? '$1' : '$4'}
                    fontWeight={fontWeightToken}
                    borderRadius={size === 'tiny' ? '$3' : '$4'}
                    borderColor={
                        invalid ? '$red600' : disabled ? '$gray600' : '$gray600'
                    }
                    color={
                        invalid ? '$red1100' : disabled ? '$gray1000' : '$black'
                    }
                    backgroundColor={
                        invalid ? '$red200' : disabled ? '$gray400' : '$gray200'
                    }
                    placeholderTextColor={
                        invalid ? '$red800' : disabled ? '$gray800' : '$gray800'
                    }
                    focusStyle={
                        isWeb
                            ? undefined
                            : {
                                  borderColor: invalid ? '$red1100' : '$black',
                                  borderWidth: 1.5,
                              }
                    }
                    hoverStyle={{
                        borderColor: disabled
                            ? '$gray600'
                            : invalid
                              ? '$red800'
                              : '$gray800',
                    }}
                    disabled={disabled}
                    editable={!disabled}
                    multiline={multiline}
                    {...borderCurve('continuous')}
                    {...props}
                />
                {rightIcon != null && (
                    <View
                        width={0}
                        height={24}
                        alignItems="center"
                        justifyContent="center"
                        style={{ transform: [{ translateX: -30 }] }}
                        onPress={onPressIcon}
                        {...(onPressIcon ? { cursor: 'pointer' } : {})}
                    >
                        <Icon icon={rightIcon} color="$gray1100" size={16} />
                    </View>
                )}
            </XStack>
        );
    },
);
